import {
  Route,
  Redirect,
  useParams,
  useLocation,
  Switch,
} from 'react-router-dom';
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { breakpoints, isMobile, useWindowSize, gutters } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import SubNavigation from 'components/Navigation/SubNavigation';
import { useTranslation } from 'react-i18next';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { settingsPagePaths } from './paths';
import { throwPermissionError } from './helpers';
import { useSelector } from 'react-redux';
import { getSettingsRoutesWithAccess } from 'store/authentication/selectors';
import routes from './routes';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { SettingsProvider } from './SettingsContext';
import { UsagePage } from './pages/Billing/UsagePage';

const MobileContainer = styled.div`
  padding: ${gutters.standard};
  text-align: center;
`;

const SettingsPage = ({ title }) => {
  const { t } = useTranslation();
  useSetTitleOnLoad(title(t));
  const { subpage } = useParams();
  const { width } = useWindowSize();
  const mobileDisplay = isMobile(width, breakpoints.lg);
  const location = useLocation();

  const { canSelfServeBilling } = useBillingPermissions();

  const settingsRoutes = useMemo(() => {
    if (canSelfServeBilling) {
      return routes;
    }
    return {
      ...routes,
      billing: {
        ...routes.billing,
        label: (t) => t('Usage'),
        icon: 'display-chart-up',
        component: UsagePage,
      },
    };
  }, [canSelfServeBilling]);

  const {
    settingsRoutesWithAccess,
    accessibleSettingsRoutes,
    firstAccesssibleSettingsRoute,
  } = useSelector((state) =>
    getSettingsRoutesWithAccess(state, settingsRoutes)
  );

  const matchedSubpage = Object.values(settingsPagePaths).find(
    (settingsPagePath) => settingsPagePath === subpage
  );

  if (!matchedSubpage) {
    if (!firstAccesssibleSettingsRoute) {
      throwPermissionError();
    }
    return <Redirect exact to={firstAccesssibleSettingsRoute.path} />;
  }

  if (mobileDisplay) {
    return (
      <MobileContainer>
        <KizenTypography>
          This page has not been optimized for mobile. Please revisit on a
          desktop to receive the best experience.
        </KizenTypography>
      </MobileContainer>
    );
  }

  return (
    <SettingsProvider>
      <SubNavigation
        style={{ marginTop: '20px' }}
        routes={accessibleSettingsRoutes}
        currentSearch={location.search}
      />
      <Switch>
        {Object.values(settingsRoutesWithAccess).map((route, index) => {
          const Component = route.component;
          return (
            <Route
              key={index}
              path={route.path}
              exact={route.exact !== false}
              render={(props) => {
                if (!route.access?.enabled) {
                  throwPermissionError();
                }
                return (
                  <Component
                    {...props}
                    access={route.access}
                    location={location}
                  />
                );
              }}
            />
          );
        })}
      </Switch>
    </SettingsProvider>
  );
};

export default SettingsPage;
